import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { AchievementToast } from '../components/AchievementToast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AchievementContext = createContext(null);

export const useAchievements = () => {
  const context = useContext(AchievementContext);
  if (!context) {
    throw new Error('useAchievements must be used within AchievementProvider');
  }
  return context;
};

export const AchievementProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);

  // Load achievements function
  const loadAchievements = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/achievements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAchievements(response.data?.achievements || []);
    } catch (error) {
      console.error('Failed to load achievements:', error);
    }
  }, [token]);

  // Load user achievements on login
  useEffect(() => {
    if (user && token) {
      loadAchievements();
    } else {
      setAchievements([]);
    }
  }, [user, token, loadAchievements]);

  // Process notification queue
  useEffect(() => {
    if (!currentNotification && pendingNotifications.length > 0) {
      const [next, ...rest] = pendingNotifications;
      setCurrentNotification(next);
      setPendingNotifications(rest);
    }
  }, [currentNotification, pendingNotifications]);

  // Check for new achievements (call this after relevant actions)
  const checkAchievements = useCallback(async (context = {}) => {
    if (!user || !token) return [];
    
    try {
      const response = await axios.post(`${API}/achievements/check`, context, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newAchievements = response.data?.newly_earned || [];
      
      if (newAchievements.length > 0) {
        // Add to achievements list
        setAchievements(prev => [...newAchievements, ...prev]);
        
        // Queue notifications
        setPendingNotifications(prev => [...prev, ...newAchievements]);
      }
      
      return newAchievements;
    } catch (error) {
      console.error('Failed to check achievements:', error);
      return [];
    }
  }, [user, token]);

  // Record game stats and check achievements
  const recordGameWin = useCallback(async (gameType, stats = {}) => {
    if (!user || !token) return;
    
    try {
      // Update game stats
      await axios.post(`${API}/games/stats`, {
        game_type: gameType,
        ...stats
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check for new achievements
      await checkAchievements({
        [`${gameType}_win`]: true,
        ...stats
      });
    } catch (error) {
      console.error('Failed to record game win:', error);
    }
  }, [user, token, checkAchievements]);

  // Dismiss current notification
  const dismissNotification = useCallback(() => {
    setCurrentNotification(null);
  }, []);

  // Show a custom achievement notification (for testing)
  const showAchievement = useCallback((achievement) => {
    setPendingNotifications(prev => [...prev, achievement]);
  }, []);

  const value = {
    achievements,
    checkAchievements,
    recordGameWin,
    showAchievement,
    loadAchievements
  };

  return (
    <AchievementContext.Provider value={value}>
      {children}
      
      {/* Achievement Toast - rendered at root level */}
      <AnimatePresence>
        {currentNotification && (
          <AchievementToast
            achievement={currentNotification}
            onClose={dismissNotification}
          />
        )}
      </AnimatePresence>
    </AchievementContext.Provider>
  );
};

export default AchievementProvider;
